import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Select, MenuItem,
  Button, TextField, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadVulnFindings, updateVulnFinding, addVulnFinding } from './vulnManagementSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';
import useCurrentPermissions from '../../hooks/useCurrentPermissions';

const SEV_COLOR = { low: tokens.severity.low, medium: tokens.severity.medium, high: tokens.severity.high, critical: tokens.severity.critical };
const STATUS_OPTIONS = ['open', 'in_remediation', 'remediated', 'accepted_risk', 'false_positive'];
const SEVERITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const emptyFinding = { title: '', cveId: '', severity: 'MEDIUM' };

function isOverdue(dueDate, status) {
  return status !== 'remediated' && status !== 'false_positive' && new Date(dueDate) < new Date();
}

export default function VulnManagementPage() {
  const dispatch = useDispatch();
  const { items, status, mutationStatus, error: storeError } = useSelector((s) => s.vulnManagement);
  const { can, loaded } = useCurrentPermissions();
  const canWrite = can('vuln:write');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyFinding);
  const [error, setError] = useState(null);

  useEffect(() => { dispatch(loadVulnFindings()); }, [dispatch]);

  const open = items.filter((f) => f.status === 'open' || f.status === 'in_remediation').length;
  const overdue = items.filter((f) => isOverdue(f.dueDate, f.status)).length;

  const handleCreate = async () => {
    if (!canWrite) return setError('Your role has read-only vulnerability access.');
    if (!form.title.trim()) return setError('Title is required.');
    if (form.cveId.trim() && !/^CVE-\d{4}-\d{4,}$/i.test(form.cveId.trim())) return setError('CVE ID must look like CVE-2026-12345.');
    setError(null);
    const result = await dispatch(addVulnFinding({ title: form.title.trim(), cveId: form.cveId.trim(), severity: form.severity }));
    if (addVulnFinding.rejected.match(result)) {
      setError(result.error?.message || 'Could not create this finding.');
      return;
    }
    setDialogOpen(false);
    setForm(emptyFinding);
  };

  return (
    <Box>
      <PageHeader
        title="Vulnerability Management"
        subtitle="Remediation lifecycle over Vulnerability Scanner findings — ownership, severity-based SLA due dates, and status through to close"
        action={<Button variant="contained" disabled={!canWrite || mutationStatus === 'loading'} startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)}>Add Finding</Button>}
      />
      {(error || storeError) && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error || storeError}</Alert>}
      {loaded && !canWrite && <Alert severity="info" sx={{ mb: 2 }}>Your role has read-only vulnerability access. Ask an administrator for vuln:write permission to create or change findings.</Alert>}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Findings" value={items.length} icon={BugReportRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Open" value={open} accent={tokens.severity.medium} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Overdue (SLA)" value={overdue} accent={tokens.severity.critical} /></Grid>
      </Grid>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Title', 'CVE', 'Severity', 'Assigned To', 'Due Date', 'Status'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((f) => {
              const overdueFlag = isOverdue(f.dueDate, f.status);
              return (
                <TableRow key={f.id} hover>
                  <TableCell sx={{ fontSize: '0.8rem', maxWidth: 260 }}>{f.title}</TableCell>
                  <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', color: tokens.text.muted }}>{f.cveId || '—'}</TableCell>
                  <TableCell><Chip label={f.severity} size="small" sx={{ color: SEV_COLOR[f.severity], border: `1px solid ${SEV_COLOR[f.severity]}`, backgroundColor: 'transparent' }} /></TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>{f.assignedTo || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.74rem', color: overdueFlag ? tokens.severity.critical : tokens.text.muted }}>
                    {formatRelativeTime(f.dueDate)}{overdueFlag ? ' (overdue)' : ''}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small" value={f.status} variant="standard" disableUnderline
                      sx={{ fontSize: '0.76rem' }} disabled={!canWrite || mutationStatus === 'loading'}
                      onChange={(e) => {
                        setError(null);
                        dispatch(updateVulnFinding({ id: f.id, changes: { status: e.target.value } })).unwrap()
                          .catch((err) => setError(err?.message || 'Could not update this finding.'));
                      }}
                    >
                      {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.78rem' }}>{s.replace(/_/g, ' ')}</MenuItem>)}
                    </Select>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Vulnerability Finding</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" size="small" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} autoFocus />
            <TextField label="CVE ID (optional)" size="small" placeholder="CVE-2026-12345" value={form.cveId} onChange={(e) => setForm((f) => ({ ...f, cveId: e.target.value }))} />
            <TextField select label="Severity" size="small" value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}>
              {SEVERITY_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!canWrite || mutationStatus === 'loading'} onClick={handleCreate}>Add Finding</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
